// Generic helpers for the app's "#section?key=val&key2=val2" hash convention (the dashboard's
// deep links, the process detail page's tab + compare state): a path-like section before the
// "?", URLSearchParams-encoded view state after it. Parsing and serialization of the param
// values is the consumers' business — this module only moves strings.
//
// Every param key is declared as a string const by its owning file and registered here in
// HashParamKey (type-only imports, so the consumer→util→consumer cycle never exists at
// runtime). Keep key strings unique within each route.
import type {
  DATE_PERIOD_HASH_KEY,
  DATE_FROM_HASH_KEY,
  DATE_TO_HASH_KEY,
  LEGACY_SESSION_DATE_FROM_HASH_KEY,
  LEGACY_SESSION_DATE_TO_HASH_KEY,
} from "../components/dashboard/dateSelection";
import type {
  PROCESS_PAGE_HASH_KEY,
  PROCESS_DURATION_MAX_HASH_KEY,
  PROCESS_DURATION_MIN_HASH_KEY,
  PROCESS_FAVOURITES_HASH_KEY,
  PROCESS_GROUPS_HASH_KEY,
  PROCESS_SHARED_HASH_KEY,
  PROCESS_PEOPLE_MIN_HASH_KEY,
  PROCESS_PEOPLE_MAX_HASH_KEY,
  PROCESS_FREQUENCY_MAX_HASH_KEY,
  PROCESS_FREQUENCY_MIN_HASH_KEY,
  PROCESS_SEARCH_HASH_KEY,
  PROCESS_SORT_HASH_KEY,
  PROCESS_STEPS_MAX_HASH_KEY,
  PROCESS_STEPS_MIN_HASH_KEY,
} from "../components/processes/ProcessesSection";
import type {
  PROCESS_USERS_HASH_KEY,
  PROCESS_FROM_HASH_KEY,
  PROCESS_TO_HASH_KEY,
} from "../screens/Processes";
import type { RECORDING_USERS_HASH_KEY } from "../screens/SessionsLayout";
import type {
  MEMBER_VIEW_HASH_KEY,
  TEAM_MEMBER_HASH_KEY,
} from "../components/dashboard/TeamActivity";
import type {
  TEAM_SEARCH_HASH_KEY,
  TEAM_SORT_HASH_KEY,
  SESSION_SEARCH_HASH_KEY,
  SESSION_ARCHIVE_HASH_KEY,
  SESSION_STATUS_HASH_KEY,
  SESSION_DURATION_MIN_HASH_KEY,
  SESSION_DURATION_MAX_HASH_KEY,
} from "../components/dashboard/useActivityFilters";

import type {
  OVERVIEW_USERS_HASH_KEY,
  OVERVIEW_FROM_HASH_KEY,
  OVERVIEW_TO_HASH_KEY,
} from "../screens/Overview";

import type {
  WATCHLIST_PEOPLE_HASH_KEY,
  WATCHLIST_PROCESSES_HASH_KEY,
  WATCHLIST_SEARCH_HASH_KEY,
  WATCHLIST_STATUS_HASH_KEY,
} from "../screens/Watchlist";

import type {
  PROCESS_COMPARE_HASH_KEY,
  PROCESS_COMPARISON_HASH_KEY,
  PROCESS_CASE_HASH_KEY,
  PROCESS_OCCURRENCE_HASH_KEY,
  PROCESS_STEP_HASH_KEY,
  PROCESS_TAB_HASH_KEY,
  ProcessScopeHashKey,
} from "../components/processes/processViewUrl";

export type HashParamKey =
  | ProcessScopeHashKey
  | typeof PROCESS_TAB_HASH_KEY
  | typeof PROCESS_STEP_HASH_KEY
  | typeof PROCESS_COMPARE_HASH_KEY
  | typeof PROCESS_COMPARISON_HASH_KEY
  | typeof PROCESS_CASE_HASH_KEY
  | typeof PROCESS_OCCURRENCE_HASH_KEY
  | typeof WATCHLIST_SEARCH_HASH_KEY
  | typeof WATCHLIST_STATUS_HASH_KEY
  | typeof WATCHLIST_PEOPLE_HASH_KEY
  | typeof WATCHLIST_PROCESSES_HASH_KEY
  | typeof TEAM_SEARCH_HASH_KEY
  | typeof TEAM_SORT_HASH_KEY
  | typeof SESSION_SEARCH_HASH_KEY
  | typeof SESSION_ARCHIVE_HASH_KEY
  | typeof SESSION_STATUS_HASH_KEY
  | typeof SESSION_DURATION_MIN_HASH_KEY
  | typeof SESSION_DURATION_MAX_HASH_KEY
  | typeof TEAM_MEMBER_HASH_KEY
  | typeof MEMBER_VIEW_HASH_KEY
  | typeof OVERVIEW_USERS_HASH_KEY
  | typeof OVERVIEW_FROM_HASH_KEY
  | typeof OVERVIEW_TO_HASH_KEY
  | typeof RECORDING_USERS_HASH_KEY
  | typeof PROCESS_USERS_HASH_KEY
  | typeof PROCESS_FROM_HASH_KEY
  | typeof PROCESS_TO_HASH_KEY
  | typeof DATE_PERIOD_HASH_KEY
  | typeof DATE_FROM_HASH_KEY
  | typeof DATE_TO_HASH_KEY
  | typeof LEGACY_SESSION_DATE_FROM_HASH_KEY
  | typeof LEGACY_SESSION_DATE_TO_HASH_KEY
  | typeof PROCESS_PAGE_HASH_KEY
  | typeof PROCESS_SEARCH_HASH_KEY
  | typeof PROCESS_SORT_HASH_KEY
  | typeof PROCESS_DURATION_MIN_HASH_KEY
  | typeof PROCESS_DURATION_MAX_HASH_KEY
  | typeof PROCESS_STEPS_MIN_HASH_KEY
  | typeof PROCESS_STEPS_MAX_HASH_KEY
  | typeof PROCESS_FREQUENCY_MIN_HASH_KEY
  | typeof PROCESS_FREQUENCY_MAX_HASH_KEY
  | typeof PROCESS_FAVOURITES_HASH_KEY
  | typeof PROCESS_GROUPS_HASH_KEY
  | typeof PROCESS_SHARED_HASH_KEY
  | typeof PROCESS_PEOPLE_MIN_HASH_KEY
  | typeof PROCESS_PEOPLE_MAX_HASH_KEY;

// A URLSearchParams whose point-lookups only accept registered keys (a compile-time view on
// the same runtime object). The params may still *contain* foreign keys — writes preserve
// them — so iteration stays string-typed; only addressing a key by name is narrowed.
export interface HashParams extends URLSearchParams {
  get(name: HashParamKey): string | null;
  getAll(name: HashParamKey): string[];
  has(name: HashParamKey, value?: string): boolean;
  set(name: HashParamKey, value: string): void;
  append(name: HashParamKey, value: string): void;
  delete(name: HashParamKey, value?: string): void;
}

export const splitHash = (hash: string): { section: string; params: HashParams } => {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const qIdx = raw.indexOf("?");
  return {
    section: qIdx === -1 ? raw : raw.slice(0, qIdx),
    params: new URLSearchParams(qIdx === -1 ? "" : raw.slice(qIdx + 1)) as HashParams,
  };
};

export const buildHash = (section: string, params: URLSearchParams): string => {
  const str = params.toString();
  if (!section && !str) return "";
  if (!str) return `#${section}`;
  return `#${section}?${str}`;
};

// The single guarded access to the live hash; consumers compose splitHash(getBrowserHash()).
export const getBrowserHash = (): string =>
  typeof window === "undefined" ? "" : window.location.hash;

// React Router stores a zero-based entry index in history state. A positive value means this
// tab has an earlier in-app entry; direct loads and new tabs start at zero and need a fallback.
export const hasInAppHistoryEntry = (): boolean => {
  if (typeof window === "undefined") return false;
  const state = window.history.state as { idx?: unknown } | null;
  return typeof state?.idx === "number" && state.idx > 0;
};

type HashUpdates = Partial<Record<HashParamKey, string | string[] | null>>;

// Pure update: set each entry in the hash's params — an array repeats the key once per value, and
// a null/undefined value deletes it. The section and unrelated params are preserved.
export const updateHash = (hash: string, updates: HashUpdates): string => {
  const { section, params } = splitHash(hash);
  const entries = Object.entries(updates) as [HashParamKey, string | string[] | null | undefined][];
  for (const [key, value] of entries) {
    if (typeof value === "string") params.set(key, value);
    else {
      params.delete(key);
      for (const item of value ?? []) params.append(key, item);
    }
  }
  return buildHash(section, params);
};

// Browser twin of updateHash. Replace-only write: hash state changes (filters, pane
// selections) shouldn't create history entries. This bypasses the router, so readers take
// getBrowserHash() as the truth.
export const updateBrowserHash = (updates: HashUpdates) => {
  if (typeof window === "undefined") return;
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}${updateHash(getBrowserHash(), updates)}`,
  );
};
